import {
	BillingInfoResponseDTO,
	BillingDataResponseDTO,
	BillingSystemResponseDTO,
	BillingDataCustomResponseDTO,
} from '@/core/api/dtos/response/accounts/billingInfo'
import {
	BillingInfo,
	BillingDataDetails,
	BillingDataCustom,
	CompanyDetails,
	BankDetails,
} from '@/core/api/entities/account/billingInfo'
import { BillingSystem } from '@/core/api/entities/account/billingSystem'

export class BillingInfoMapper {
	static toEntity(dto: BillingInfoResponseDTO): BillingInfo {
		return BillingInfo.create({
			alegraIso: dto.alegra_iso || '',
			paymentGateway: dto.payment_gateway || '',
			paymentMethod: dto.payment_method || '',
			companyDetails: this.mapCompanyDetails(dto.company_details),
			bankDetails: this.mapBankDetails(dto.bank_details),
			tax: dto.tax || 0,
			billingData: dto.billing_data
				? this.mapBillingData(dto.billing_data)
				: null,
			billingSystem: dto.billing_system
				? this.mapBillingSystem(dto.billing_system)
				: null,
		})
	}

	private static mapCompanyDetails(
		dto: BillingInfoResponseDTO['company_details']
	): CompanyDetails {
		if (!dto) {
			return {
				tax: '',
				phone: '',
				email: '',
				address: '',
				name: '',
			}
		}
		return {
			tax: dto.tax || '',
			phone: dto.phone || '',
			email: dto.email || '',
			address: dto.address || '',
			name: dto.name || '',
		}
	}

	private static mapBankDetails(
		dto: BillingInfoResponseDTO['bank_details']
	): BankDetails {
		if (!dto) {
			return {
				bankName: '',
				taxId: '',
				type: '',
				accountName: '',
				accountNumber: '',
				fullAccountNumber: '',
				currency: '',
				swift: '',
				address: '',
				payTo: '',
				routing: '',
				alias: '',
				clabe: '',
				finalCreditAccount: '',
			}
		}
		return {
			bankName: dto.bank_name || '',
			taxId: dto.tax_id || '',
			type: dto.type || '',
			accountName: dto.account_name || '',
			accountNumber: dto.account_number || '',
			fullAccountNumber: dto.full_account_number || '',
			currency: dto.currency || '',
			swift: dto.swift || '',
			address: dto.address || '',
			payTo: dto.pay_to || '',
			routing: dto.routing || '',
			alias: dto.alias || '',
			clabe: dto.clabe || '',
			finalCreditAccount: dto.final_credit_account || '',
		}
	}

	private static mapBillingDataCustom(
		dto: BillingDataCustomResponseDTO
	): BillingDataCustom {
		return {
			municipality: dto.municipality,
			extNumber: dto.ext_number,
			intNumber: dto.int_number,
			contabiliumFcType: dto.contabilium_fc_type,
			alegraPaymentMethod: dto.alegra_payment_method,
			alegraPaymentType: dto.alegra_payment_type,
			alegraCurrency: dto.alegra_currency,
			invoiceLanguage: dto.invoice_language,
			alegraIdentificationType: dto.alegra_identification_type,
			alegraIvaCondition: dto.alegra_iva_condition,
			alegraSaleCondition: dto.alegra_sale_condition,
			alegraSaleConcept: dto.alegra_sale_concept,
			alegraInvoiceType: dto.alegra_invoice_type,
			alegraPaymentForm: dto.alegra_payment_form,
			alegraEconomicActivity: dto.alegra_economic_activity,
			alegraCommune: dto.alegra_commune,
			alegraRegion: dto.alegra_region,
			alegraGiro: dto.alegra_giro,
			regime: dto.regime,
			useInvoiceNotifications: dto.use_invoice_notifications || false,
			sendOcEmailAutomatically: dto.send_oc_email_automatically || false,
			ocNotificationEmails: dto.oc_notification_emails,
			contactName: dto.contact_name,
			contactPhone: dto.contact_phone,
			alegraIdentificationVerificationDigit:
				dto.alegra_identification_verification_digit,
			alegraKindOfPerson: dto.alegra_kind_of_person,
			alegraRegime: dto.alegra_regime,
			alegraFirstname: dto.alegra_firstname,
			alegraLastname: dto.alegra_lastname,
			alegraTaxReason: dto.alegra_tax_reason,
			alegraTaxCause: dto.alegra_tax_cause,
		}
	}

	private static mapBillingData(
		dto: BillingDataResponseDTO
	): BillingDataDetails {
		return {
			id: dto.id,
			officialCompanyName: dto.official_company_name || '',
			tax: dto.tax || '',
			country: dto.country || '',
			state: dto.state || '',
			city: dto.city || '',
			street: dto.street || '',
			zip: dto.zip || '',
			custom: dto.custom
				? this.mapBillingDataCustom(dto.custom)
				: {
						municipality: null,
						extNumber: null,
						intNumber: null,
						contabiliumFcType: null,
						alegraPaymentMethod: null,
						alegraPaymentType: null,
						alegraCurrency: null,
						invoiceLanguage: null,
						alegraIdentificationType: null,
						alegraIvaCondition: null,
						alegraSaleCondition: null,
						alegraSaleConcept: null,
						alegraInvoiceType: null,
						alegraPaymentForm: null,
						alegraEconomicActivity: null,
						alegraCommune: null,
						alegraRegion: null,
						alegraGiro: null,
						regime: null,
						useInvoiceNotifications: false,
						sendOcEmailAutomatically: false,
						ocNotificationEmails: null,
						contactName: null,
						contactPhone: null,
						alegraIdentificationVerificationDigit: null,
						alegraKindOfPerson: null,
						alegraRegime: null,
						alegraFirstname: null,
						alegraLastname: null,
						alegraTaxReason: null,
						alegraTaxCause: null,
					},
			notificationEmails: dto.notification_emails || [],
			billingSystemId: dto.billing_system_id || 0,
		}
	}

	private static mapBillingSystem(
		dto: BillingSystemResponseDTO
	): BillingSystem {
		return BillingSystem.create({
			id: dto.id,
			name: dto.name || '',
			country: dto.country || '',
		})
	}
}
